<template>
  <div>
    <Head title="Gastos" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <h1 class="text-3xl font-bold text-gray-800">Gestión de Gastos</h1>
      
      <div class="flex flex-wrap items-center gap-3">
        <!-- Print Report -->
        <a
          target="_blank" href="/spents/monthly-report/pdf" 
          class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
          title="Imprimir reporte mensual"
        >
          <i class="fa fa-print text-red-600" />
          Reporte Mensual
        </a>

        <!-- Create Button -->
        <Link
          href="/spents/create" 
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-plus" />
          Nuevo Gasto
        </Link>
      </div>
    </div>

    <!-- Stats Dashboard -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div class="bg-gradient-to-br from-teal-500 to-teal-600 p-6 rounded-2xl shadow-lg border border-teal-400 relative overflow-hidden group">
        <div class="absolute -right-4 -bottom-4 text-8xl text-white/10 group-hover:scale-110 transition-transform duration-500">
          <i class="fa fa-calendar-check" />
        </div>
        <div class="flex flex-col relative z-10">
          <span class="text-teal-100 text-sm font-bold uppercase tracking-wider mb-1">Meses Registrados</span>
          <span class="text-3xl font-bold text-white leading-none">
            {{ monthlyReport.length }}
          </span>
        </div>
      </div>

      <div class="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 relative overflow-hidden group">
        <div class="absolute -right-4 -bottom-4 text-8xl text-gray-50 group-hover:scale-110 transition-transform duration-500">
          <i class="fa fa-money-bill-wave" />
        </div>
        <div class="flex flex-col relative z-10">
          <span class="text-gray-400 text-sm font-bold uppercase tracking-wider mb-1">Total Gastado</span>
          <span class="text-3xl font-bold text-gray-900 leading-none">
            {{ formatCurrency(totalSpent) }}
          </span>
        </div>
      </div>

      <div class="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 relative overflow-hidden group">
        <div class="absolute -right-4 -bottom-4 text-8xl text-gray-50 group-hover:scale-110 transition-transform duration-500">
          <i class="fa fa-chart-line" />
        </div>
        <div class="flex flex-col relative z-10">
          <span class="text-gray-400 text-sm font-bold uppercase tracking-wider mb-1">Mes más alto</span>
          <span class="text-3xl font-bold text-gray-900 leading-none">
            {{ formatCurrency(maxSpent) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Search & Chart Section -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
      <div class="lg:col-span-1 space-y-6">
        <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h2 class="text-sm font-bold text-gray-800 mb-4 uppercase tracking-tighter">Búsqueda y Filtros</h2>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i class="fa fa-search text-gray-400" />
            </div>
            <input 
              v-model="form.search" 
              type="text" 
              placeholder="Nombre o categoría..."
              class="block w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-teal-500 focus:border-teal-500 text-sm transition-colors"
            />
          </div>
          <button class="w-full mt-4 py-2 text-sm font-medium text-gray-400 hover:text-teal-600 transition-colors" @click="reset">
            Limpiar búsqueda
          </button>
        </div>

        <div v-if="monthlyReport.length > 0" class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hidden lg:block">
          <h2 class="text-sm font-bold text-gray-800 mb-4 uppercase tracking-tighter">Tendencia de Gastos</h2>
          <canvas id="monthlySpentChart" height="200" />
        </div>
      </div>

      <div class="lg:col-span-2">
        <!-- Table Desktop -->
        <div class="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table class="w-full text-left">
            <thead>
              <tr class="bg-gray-50/50">
                <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Concepto</th>
                <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Categoría</th>
                <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Fecha</th>
                <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Monto</th>
                <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-50">
              <tr v-for="spent in spents.data" :key="spent.id" class="hover:bg-teal-50/30 transition-colors group">
                <td class="py-4 px-6 font-semibold text-gray-800 group-hover:text-teal-700 transition-colors">
                  {{ spent.name }}
                </td>
                <td class="py-4 px-6">
                  <span class="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-bold uppercase">
                    {{ spent.category.name }}
                  </span>
                </td>
                <td class="py-4 px-6 text-sm text-gray-500 font-medium">
                  {{ formatDate(spent.date) }}
                </td>
                <td class="py-4 px-6 text-right font-black text-gray-900">
                  <div class="flex flex-col items-end">
                    <span>{{ formatCurrency(spent.amount) }}</span>
                    <span v-if="spent.currency === 'BS'" class="text-[10px] text-gray-400 font-medium">
                      Bs. {{ formatNumber(spent.amount * spent.exchange_rate) }}
                    </span>
                  </div>
                </td>
                <td class="py-4 px-6 text-center">
                  <Link
                    :href="`/spents/${spent.id}/edit`" 
                    class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                  >
                    <i class="fa fa-edit" />
                  </Link>
                </td>
              </tr>
              <tr v-if="spents.data.length === 0">
                <td colspan="5" class="py-20 text-center text-gray-400 italic">
                  No hay gastos registrados que coincidan
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Cards -->
        <div class="grid grid-cols-1 gap-4 md:hidden">
          <div
            v-for="spent in spents.data" :key="spent.id" 
            class="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col gap-3"
          >
            <div class="flex justify-between items-start">
              <span class="text-xs font-bold text-teal-600 uppercase tracking-widest">{{ spent.category.name }}</span>
              <span class="text-xs text-gray-400">{{ formatDateShort(spent.date) }}</span>
            </div>
            <h3 class="font-semibold text-gray-900 leading-tight">{{ spent.name }}</h3>
            <div class="flex items-end justify-between mt-1">
              <div class="flex flex-col">
                <span class="text-xl font-bold text-gray-900">{{ formatCurrency(spent.amount) }}</span>
                <span v-if="spent.currency === 'BS'" class="text-xs text-gray-400 font-normal tracking-tight">
                  Bs. {{ formatNumber(spent.amount * spent.exchange_rate) }}
                </span>
              </div>
              <Link :href="`/spents/${spent.id}/edit`" class="text-xs font-medium text-teal-600 flex items-center gap-1">
                Editar
                <i class="fa fa-chevron-right text-[10px]" />
              </Link>
            </div>
          </div>
          <div v-if="spents.data.length === 0" class="py-10 text-center text-gray-400 italic bg-white rounded-2xl border border-gray-100 px-6">
            No se encontraron gastos
          </div>
        </div>

        <pagination v-if="spents.meta.links" :links="spents.meta.links" class="mt-6" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import Chart from 'chart.js/auto'

export default {
  name: 'SpentsIndex',
  
  components: {
    Head,
    Link,
    Pagination,
  },

  layout: Layout,

  props: {
    filters: Object,
    spents: Object,
  },

  data() {
    return {
      form: {
        search: this.filters.search || '',
      },
      monthlyReport: [],
      totalSpent: 0,
      maxSpent: 0,
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get('/spents', pickBy(this.form), { 
          preserveState: true, 
        })
      }, 300),
    },
  },

  mounted() {
    this.fetchMonthlyReport()
  },

  methods: {
    back() {
      this.$inertia.visit('/')
    },

    reset() {
      this.form.search = ''
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    formatDateShort(date) {
      return new Date(date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },

    formatNumber(value) {
      return new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value || 0)
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('es-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value || 0)
    },

    fetchMonthlyReport() {
      fetch('/spents/monthly-report')
        .then(res => res.json())
        .then(data => {
          this.monthlyReport = data
          this.totalSpent = data.reduce((acc, curr) => acc + Number(curr.total), 0)
          this.maxSpent = data.length ? Math.max(...data.map(i => Number(i.total))) : 0
          this.$nextTick(() => {
            this.renderChart()
          })
        })
    },

    renderChart() {
      if (this._chart) this._chart.destroy()
      const ctx = document.getElementById('monthlySpentChart')
      if (!ctx) return
      this._chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.monthlyReport.map(i => i.month),
          datasets: [{
            label: 'Gasto mensual',
            data: this.monthlyReport.map(i => i.total),
            borderColor: '#0d9488',
            backgroundColor: 'rgba(13, 148, 136, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: '#0d9488',
          }],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
          },
          scales: {
            y: { 
              beginAtZero: true,
              grid: { display: false },
            },
            x: {
              grid: { display: false },
            },
          },
        },
      })
    },
  },
}
</script>
